import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

export interface CaseDocRef {
  case_id: string;
  storageName: string;
  originalName: string;
}

export type PreviewKind = "pdf" | "image" | "text" | "none";

const IMAGE_EXT = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp"]);
const TEXT_EXT = new Set(["txt", "csv", "log", "md", "json", "xml", "html", "htm", "svg"]);

// Must agree with INLINE_CONTENT_TYPES in thrive_server/apps/upload/upload_router.ts
export const previewKind = (name: string): PreviewKind => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (ext === "pdf") return "pdf";
  if (IMAGE_EXT.has(ext)) return "image";
  if (TEXT_EXT.has(ext)) return "text";
  return "none";
};

export const caseDocUrl = (doc: CaseDocRef, inline = false) =>
  `${process.env.REACT_APP_API_DOMAIN}/upload/case-doc/${doc.case_id}/${
    doc.storageName
  }/${encodeURIComponent(doc.originalName)}${inline ? "?inline=1" : ""}`;

export const downloadCaseDoc = (doc: CaseDocRef) => {
  window.open(caseDocUrl(doc), "_blank");
};

/**
 * In-app viewer for an uploaded case document. PDFs, images and plain-text
 * files render inline (served with `?inline=1`); other types show a
 * "no preview" notice with a download button.
 */
const FilePreviewDialog: React.FC<{
  doc: CaseDocRef | null;
  onClose: () => void;
}> = ({ doc, onClose }) => {
  const kind = doc ? previewKind(doc.originalName) : "none";
  const inlineUrl = doc ? caseDocUrl(doc, true) : "";

  return (
    <Dialog
      open={!!doc}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pr: 1,
          py: 1,
        }}
      >
        <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
        <Typography
          component="span"
          sx={{
            flex: 1,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={doc?.originalName}
        >
          {doc?.originalName}
        </Typography>
        {doc && kind !== "none" && (
          <IconButton
            size="small"
            title="Open in new tab"
            onClick={() => window.open(inlineUrl, "_blank")}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        )}
        {doc && (
          <IconButton
            size="small"
            title="Download"
            onClick={() => downloadCaseDoc(doc)}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton size="small" onClick={onClose} title="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 0,
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          backgroundColor: kind === "image" ? "#f5f5f5" : "background.paper",
          overflow: "auto",
        }}
      >
        {doc && (kind === "pdf" || kind === "text") && (
          <iframe
            key={inlineUrl}
            src={inlineUrl}
            title={doc.originalName}
            style={{ border: 0, width: "100%", height: "100%", flex: 1 }}
          />
        )}
        {doc && kind === "image" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              p: 2,
            }}
          >
            <img
              src={inlineUrl}
              alt={doc.originalName}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          </Box>
        )}
        {doc && kind === "none" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              width: "100%",
              p: 4,
              textAlign: "center",
            }}
          >
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 56, color: "text.disabled" }} />
            <Typography variant="body1">
              This file type can't be previewed in the browser.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Download it to open in the matching application.
            </Typography>
            <Button
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() => downloadCaseDoc(doc)}
            >
              Download
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ py: 0.5 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePreviewDialog;
